import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Mutation,
  MutationAddRestApiKeyArgs,
  MutationDeleteRestApiKeyArgs,
  MutationUpdateRestApiKeyArgs,
  Query,
  RestApiKey
} from 'graphql/generated/graphqlTypes';
import { ADD_REST_API_KEY, DELETE_REST_API_KEY, UPDATE_REST_API_KEY } from 'graphql/v2/mutations';
import { GET_REST_API_KEYS } from 'graphql/v2/queries';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import { useEffect } from 'react';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { TranslatedHookProps } from '../hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_REST_API_KEYS_TYPED: TypedDocumentNode<Pick<Query, 'getRestApiKeys'>> = GET_REST_API_KEYS;
const ADD_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addRestApiKey'>,
  MutationAddRestApiKeyArgs
> = ADD_REST_API_KEY;
const UPDATE_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateRestApiKey'>,
  MutationUpdateRestApiKeyArgs
> = UPDATE_REST_API_KEY;
const DELETE_REST_API_KEY_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteRestApiKey'>,
  MutationDeleteRestApiKeyArgs
> = DELETE_REST_API_KEY;

const useApiKeys = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const { loading, error, data } = useQuery(GET_REST_API_KEYS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (error) {
      handleFetchError(translation.api_key_error_fetching);
      console.error(error);
    }
  }, [error]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingApiKeys = !!data && loading;

  useRegisterBackgroundRefetch(isRefetchingApiKeys);

  const [addRestApiKeyMutation] = useMutation(ADD_REST_API_KEY_TYPED, {
    refetchQueries: [{ query: GET_REST_API_KEYS }],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.api_key_error_adding)
  });

  const [updateRestApiKeyMutation] = useMutation(UPDATE_REST_API_KEY_TYPED, {
    refetchQueries: [{ query: GET_REST_API_KEYS }],
    awaitRefetchQueries: true
  });

  const [deleteRestApiKeyMutation] = useMutation(DELETE_REST_API_KEY_TYPED, {
    refetchQueries: [{ query: GET_REST_API_KEYS }],
    awaitRefetchQueries: true
  });

  const addApiKey = async (description: string): Promise<RestApiKey | undefined> => {
    const { data: mutationData } = await ignoreHandledMutationError(
      addRestApiKeyMutation({ variables: { description } })
    );
    return mutationData?.addRestApiKey;
  };

  const updateApiKey = async (id: string, description: string): Promise<boolean> => {
    try {
      const { data: mutationData } = await updateRestApiKeyMutation({ variables: { id, description } });
      return !!mutationData?.updateRestApiKey;
    } catch {
      return false;
    }
  };

  const deleteApiKey = async (id: string): Promise<boolean> => {
    try {
      const { data: mutationData } = await deleteRestApiKeyMutation({ variables: { id } });
      return !!mutationData?.deleteRestApiKey;
    } catch {
      return false;
    }
  };

  return {
    restApiKeys: data?.getRestApiKeys ?? [],
    loading: loading && !data,
    isRefetchingApiKeys,
    addApiKey,
    updateApiKey,
    deleteApiKey
  };
};

export default useApiKeys;
